import { SentryTraced } from "@sentry/nestjs"
import fs from "node:fs"
import path from "node:path"
import { fileURLToPath } from "node:url"

import { AnalyzeCommandBase, analyzeFlags } from "../../utils/analyze-command.js"

const fallbackAnalyzeHelpDescription = [
  "Analyze components and package adoption for one or more packages.",
  "Runs both scanners (`components` and `usage`) for each package target.",
  "By default, generated snapshots are uploaded to Supernova and processing is tracked until completion.",
  "Use `--dryRun` to keep results local only and skip upload, or `--noWait` to exit after processing starts.",
].join("\n")

const loadAnalyzeHelpDescription = (): string => {
  const commandFilePath = fileURLToPath(import.meta.url)
  const docsPath = path.resolve(path.dirname(commandFilePath), "../../../docs/code-analysis.md")

  try {
    return fs.readFileSync(docsPath, "utf8").trim()
  } catch {
    return fallbackAnalyzeHelpDescription
  }
}

export default class Analyze extends AnalyzeCommandBase {
  static override description = loadAnalyzeHelpDescription()
  static override examples = [
    "<%= config.bin %> <%= command.id %> --package @acme/ds --dryRun",
    "<%= config.bin %> <%= command.id %> --designSystemId 1234 --package @acme/ds",
    "<%= config.bin %> <%= command.id %> --package @acme/ds --package @acme/icons",
    "<%= config.bin %> <%= command.id %> --package @acme/ds --exclude @acme/utils",
    "<%= config.bin %> code analyze status --designSystemId 1234",
  ]
  static override flags = analyzeFlags

  @SentryTraced()
  public async run(): Promise<void> {
    const { flags } = await this.parse(Analyze)

    if (this.isWizardMode(flags)) {
      await this.promptIntro()
      const selectedDesignSystem = flags.designSystemId
        ? await this.fetchDesignSystemById(flags.designSystemId)
        : await this.promptDesignSystemWizard()
      flags.designSystemId = selectedDesignSystem.id
      const discovered = await this.promptSourceScanIntro()

      while (true) {
        flags.package = await this.promptPackageSelection(discovered)
        const scope = await this.promptScanScope()
        flags.exclude =
          scope === "select" ? await this.promptExcludePackageSelection(discovered) : undefined
        const result = await this.executeAnalyze(flags, "all", true, selectedDesignSystem)
        if (result === "completed") break
      }

      return
    }

    await this.executeAnalyze(flags, "all")
  }
}
